import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getWatchlist,
  saveWatchlistItem,
  submitWatchlistAnswer,
  type WatchItem,
  type WatchCategory,
} from "@/lib/api/endpoints/profile-interview";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import "./WatchlistPanel.css";

const TALK_SEED =
  "What do you want me to keep an eye on? Tell me the prices, events, and topics you care about — e.g. “Bitcoin and gold prices”, “the SpaceX IPO”, “news on Anthropic and Michael Saylor”. I'll start a watchlist (and pull news on each, soon).";

const CAT_ICON: Record<WatchCategory, string> = {
  price: "📈",
  event: "📅",
  topic: "📰",
};
const CATS: WatchCategory[] = ["price", "event", "topic"];

export function WatchlistPanel() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "watchlist");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => getWatchlist(token, ws),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });
  const [talking, setTalking] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<{ label: string; category: WatchCategory; note: string }>({
    label: "",
    category: "topic",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const startAdd = () => {
    setForm({ label: "", category: "topic", note: "" });
    setEditId("__new");
  };
  const startEdit = (it: WatchItem) => {
    setForm({ label: it.label, category: it.category, note: it.note ?? "" });
    setEditId(it.id);
  };
  const save = async () => {
    const label = form.label.trim();
    if (!label) return;
    setSaving(true);
    try {
      const item: Record<string, unknown> =
        editId === "__new"
          ? { label, category: form.category, note: form.note.trim() }
          : { id: editId, label, category: form.category, note: form.note.trim() };
      queryClient.setQueryData(
        queryKey,
        await saveWatchlistItem(token ?? null, ws, {
          op: editId === "__new" ? "add" : "edit",
          item,
        }),
      );
      setEditId(null);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: string) => {
    setSaving(true);
    try {
      queryClient.setQueryData(
        queryKey,
        await saveWatchlistItem(token ?? null, ws, { op: "remove", item: { id } }),
      );
      setEditId(null);
    } finally {
      setSaving(false);
    }
  };

  if (isError && !data)
    return (
      <div className="wl-empty">
        Couldn't load the watchlist.{" "}
        <button onClick={() => void refetch()}>Retry</button>
      </div>
    );
  if (isLoading || !data) return <div className="wl-empty">Loading…</div>;
  const items = data.items;

  const editor = (id: string) => (
    <div className="wl-edit" key={`edit-${id}`}>
      <input
        className="wl-edit-in"
        autoFocus
        value={form.label}
        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
        placeholder="What to watch (e.g. Bitcoin, SpaceX IPO)"
      />
      <div className="wl-edit-row">
        <select
          className="wl-edit-in"
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as WatchCategory }))}
        >
          {CATS.map((c) => (
            <option key={c} value={c}>
              {CAT_ICON[c]} {c}
            </option>
          ))}
        </select>
        <input
          className="wl-edit-in"
          value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          placeholder="note (optional)"
        />
      </div>
      <div className="wl-edit-actions">
        <button onClick={() => setEditId(null)} disabled={saving}>
          Cancel
        </button>
        {id !== "__new" && (
          <button className="wl-edit-del" onClick={() => void remove(id)} disabled={saving}>
            Delete
          </button>
        )}
        <button className="wl-edit-save" onClick={() => void save()} disabled={saving || !form.label.trim()}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="wl-wrap">
      <div className="wl-head">
        <button className="people-add-btn people-talk-btn" onClick={() => setTalking(true)}>
          💬 Talk to Xavior about your watchlist
        </button>
        <button className="wl-add" onClick={startAdd}>
          ＋ Add
        </button>
      </div>

      {editId === "__new" && editor("__new")}

      {items.length === 0 && editId !== "__new" ? (
        <div className="wl-empty">
          Nothing tracked yet — add prices, events, or topics to watch.
        </div>
      ) : (
        <div className="wl-list">
          {items.map((it) =>
            editId === it.id ? (
              editor(it.id)
            ) : (
              <button key={it.id} type="button" className="wl-row" onClick={() => startEdit(it)}>
                <span className="wl-row-icon">{CAT_ICON[it.category]}</span>
                <span className="wl-row-label">{it.label}</span>
                {it.note && <span className="wl-row-note">{it.note}</span>}
              </button>
            ),
          )}
        </div>
      )}

      {talking && (
        <InterviewPanel
          seedQuestion={TALK_SEED}
          title="Talk to Xavior about your watchlist"
          onAnswer={submitWatchlistAnswer}
          continuous
          onClose={() => setTalking(false)}
          onComplete={() => void refetch()}
        />
      )}
    </div>
  );
}
